import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import toast from "react-hot-toast";
import { AlertTriangle, Clock, Loader2, RefreshCw, X } from "lucide-react";
import { ROUTES } from "@/routes";
import {
  topicLabel,
  type SupportTicketStatus,
} from "@/application/support/types";
import { SupportRequestsRepository } from "@/application/support/support_repository";
import { SupportStatusBadge } from "./SupportStatusBadge";

const client = generateClient<Schema>();
const repo = new SupportRequestsRepository();

interface Ticket {
  id: string;
  reference: string;
  topic: string;
  status: SupportTicketStatus;
  description: string;
  businessId: string | null;
  userId: string;
  context: string | null;
  hubspotTicketId: string | null;
  hubspotContactId: string | null;
  syncError: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface Props {
  ticketId: string;
  onClose: () => void;
  /** Lets the list refresh a row whose status this sheet just changed. */
  onRetried?: () => void;
}

// ── Diagnostics rendering ─────────────────────────────────────────────────────

const ACCOUNT_FIELDS: { key: string; label: string }[] = [
  { key: "businessName", label: "Business name" },
  { key: "email", label: "Email" },
  { key: "subscriptionPlan", label: "Plan" },
  { key: "subscriptionStatus", label: "Subscription" },
  { key: "chargesStatus", label: "Charges" },
  { key: "identityVerificationStatus", label: "Identity verification" },
  { key: "payoutsEnabled", label: "Payouts enabled" },
  { key: "hasBankAccount", label: "Bank account" },
  { key: "businessCountry", label: "Country" },
  { key: "stripeConnectedAccountId", label: "Connected account" },
];

const DEVICE_FIELDS: { key: string; label: string }[] = [
  { key: "appVersion", label: "App version" },
  { key: "deviceModel", label: "Device" },
  { key: "osName", label: "OS" },
  { key: "osVersion", label: "OS version" },
];

function formatValue(value: unknown): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value == null || value === "") return "—";
  return String(value);
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function DiagnosticsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right font-medium break-all">
        {value}
      </span>
    </div>
  );
}

function DiagnosticsSection({
  title,
  fields,
  context,
}: {
  title: string;
  fields: { key: string; label: string }[];
  context: Record<string, unknown>;
}) {
  const present = fields.filter((f) => f.key in context);
  if (present.length === 0) return null;
  return (
    <div>
      <h4 className="text-subtle-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
        {title}
      </h4>
      <div className="divide-border divide-y">
        {present.map((f) => (
          <DiagnosticsRow
            key={f.key}
            label={f.label}
            value={formatValue(context[f.key])}
          />
        ))}
      </div>
    </div>
  );
}

function Diagnostics({ raw }: { raw: string | null }) {
  const [showRaw, setShowRaw] = useState(false);

  if (!raw) {
    return (
      <p className="text-muted-foreground text-sm">No diagnostics captured.</p>
    );
  }

  let parsed: Record<string, unknown> | null = null;
  try {
    const value = JSON.parse(raw);
    parsed = value && typeof value === "object" ? value : null;
  } catch {
    parsed = null;
  }

  if (!parsed) {
    return (
      <pre className="overflow-x-auto rounded bg-slate-900 p-2 text-[11px] text-slate-100">
        {raw}
      </pre>
    );
  }

  return (
    <div className="space-y-4">
      <DiagnosticsSection
        title="Account"
        fields={ACCOUNT_FIELDS}
        context={parsed}
      />
      <DiagnosticsSection
        title="Device"
        fields={DEVICE_FIELDS}
        context={parsed}
      />
      <button
        type="button"
        onClick={() => setShowRaw((v) => !v)}
        className="text-xs text-blue-600 hover:underline"
      >
        {showRaw ? "Hide raw JSON" : "Show raw JSON"}
      </button>
      {showRaw && (
        <pre className="overflow-x-auto rounded bg-slate-900 p-2 text-[11px] text-slate-100">
          {JSON.stringify(parsed, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── Sheet ─────────────────────────────────────────────────────────────────────

export function SupportDetailSheet({ ticketId, onClose, onRetried }: Props) {
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setTicket(null);
      setError(null);
      try {
        const { data, errors } = await client.models.SupportTicket.get({
          id: ticketId,
        });
        if (errors?.length) throw new Error(errors[0].message);
        if (!cancelled) setTicket((data as Ticket | null) ?? null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load ticket",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [ticketId, reloadKey]);

  /**
   * Re-runs the HubSpot sync for this one request.
   *
   * Every outcome arrives as data, so the toast can say what actually happened
   * rather than just "failed" — a request another operator already repaired and
   * one HubSpot rejected again are not the same news. The row is refetched
   * either way, because a repeat failure rewrites `syncError` with the new
   * reason and that is the more useful thing on screen.
   */
  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      const result = await repo.retrySync(ticketId);

      if (result.outcome === "synced") {
        toast.success("Sent to HubSpot");
      } else if (result.outcome === "already-synced") {
        toast.success(result.message ?? "Already in HubSpot");
      } else if (result.outcome === "unrecorded") {
        // HubSpot has it; only our status write missed. Saying "failed" here
        // would send an operator looking for a ticket that already exists.
        toast.success(result.message ?? "Reached HubSpot, status not saved");
      } else {
        toast.error(result.message ?? "HubSpot sync failed again");
      }

      setReloadKey((key) => key + 1);
      onRetried?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Retry failed");
    } finally {
      setRetrying(false);
    }
  }, [ticketId, onRetried]);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/10"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="border-border bg-card fixed top-0 right-0 bottom-0 z-50 flex w-[560px] max-w-[95vw] flex-col border-l shadow-xl">
        <div className="border-border flex items-start justify-between border-b px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-foreground font-mono text-lg font-semibold">
                {ticket?.reference ?? "Support request"}
              </h2>
              {ticket && <SupportStatusBadge status={ticket.status} />}
            </div>
            {ticket && (
              <p className="text-muted-foreground mt-1 text-xs">
                {topicLabel(ticket.topic)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-subtle-foreground hover:bg-muted hover:text-muted-foreground rounded p-1"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && !ticket && (
            <div className="text-muted-foreground text-sm">
              Ticket not found.
            </div>
          )}

          {!loading && ticket && (
            <>
              {/* Sync outcome */}
              {ticket.status === "Failed" && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3.5 py-3">
                  <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-red-800 uppercase">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    HubSpot sync failed
                  </div>
                  {ticket.syncError && (
                    <p className="mt-1.5 text-xs break-words text-red-900">
                      {ticket.syncError}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleRetry()}
                    disabled={retrying}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {retrying ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    {retrying ? "Retrying…" : "Retry HubSpot sync"}
                  </button>
                </div>
              )}

              {ticket.status === "Pending" && (
                <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs font-medium text-amber-800">
                  <Clock className="h-3.5 w-3.5" />
                  Awaiting sync to HubSpot
                </div>
              )}

              {ticket.status === "Synced" && (
                <div className="rounded-md border border-green-200 bg-green-50 px-3.5 py-3 text-sm">
                  <div className="space-y-1">
                    {ticket.hubspotTicketId && (
                      <div className="flex justify-between gap-3">
                        <span className="text-green-700">HubSpot ticket</span>
                        <span className="font-mono text-xs break-all text-green-900">
                          {ticket.hubspotTicketId}
                        </span>
                      </div>
                    )}
                    {ticket.hubspotContactId && (
                      <div className="flex justify-between gap-3">
                        <span className="text-green-700">HubSpot contact</span>
                        <span className="font-mono text-xs break-all text-green-900">
                          {ticket.hubspotContactId}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Requester */}
              <div>
                <h4 className="text-subtle-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
                  Requester
                </h4>
                <div className="divide-border divide-y">
                  <div className="flex justify-between gap-3 py-1 text-sm">
                    <span className="text-muted-foreground">Merchant</span>
                    {ticket.businessId ? (
                      <Link
                        to={ROUTES.MERCHANT_DETAIL.replace(
                          ":businessId",
                          ticket.businessId,
                        )}
                        className="text-right font-medium break-all text-blue-600 hover:underline"
                      >
                        {ticket.businessId}
                      </Link>
                    ) : (
                      <span className="text-subtle-foreground">—</span>
                    )}
                  </div>
                  <div className="flex justify-between gap-3 py-1 text-sm">
                    <span className="text-muted-foreground">User</span>
                    <span className="text-foreground text-right font-mono text-xs break-all">
                      {ticket.userId || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3 py-1 text-sm">
                    <span className="text-muted-foreground">Created</span>
                    <span className="text-foreground text-right">
                      {formatTimestamp(ticket.createdAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="text-subtle-foreground mb-1 text-xs font-semibold tracking-wide uppercase">
                  Message
                </h4>
                <p className="border-border bg-muted text-foreground rounded-lg border px-3 py-2 text-sm whitespace-pre-wrap">
                  {ticket.description || "—"}
                </p>
              </div>

              {/* Diagnostics */}
              <div>
                <h4 className="text-subtle-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                  Diagnostics
                </h4>
                <Diagnostics raw={ticket.context} />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
